// Client for the backend's legal AI endpoints (backend/api/legalAi.js).
// Every response is a labeled AI suggestion — the UI must present it as such
// and require explicit human action to apply anything to the official draft.
import firebase, { db } from '../firebaseConfig';

const API = process.env.REACT_APP_UPLOAD_API || '';

export const isAiConfigured = () => !!API;

async function post(path, body) {
  const user = firebase.auth().currentUser;
  const token = user ? await user.getIdToken() : null;
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `${path} failed (${res.status})`);
  return data;
}

export const aiSummarize = (params) => post('/legal/summarize', params);
export const aiCompare = (params) => post('/legal/compare', params);
export const aiCompromise = (params) => post('/legal/compromise', params);
export const aiDraft = (params) => post('/legal/draft', params);
export const aiRisks = (params) => post('/legal/risks', params);
// Deterministic checkpoint-chain verification (no AI).
export const verifyChain = (params) => post('/legal/verify', params);
// Jurisdictional compliance screen — result is also stored under
// docs/{id}/assessments for history.
export const aiJurisdiction = (params) => post('/legal/jurisdiction', params);
// Drafting agent: with docId → edit proposal (pending, human applies);
// without docId → creates a whole structured document from the prompt.
//
// The server answers every edit / create / whole-project instruction with
// a QUEUED run ({ queued, runPath }) and finishes it in the background —
// the run survives the HTTP request, this tab, and a server deploy (the
// backend resumes orphaned runs). Callers keep the old synchronous shape:
// we watch the run doc and resolve with its `result` (or throw its error).
// Whole-project runs (`dealEdit`) are returned immediately — the CommandBar
// renders those live.
export async function aiDocAgent(params) {
  // `runs: true` — this client understands queued runs and awaits the doc
  // itself; without it the server waits inline for older tabs.
  const r = await post('/legal/agent', { ...params, runs: true });
  if (!r?.queued || !r.runPath || r.dealEdit) return r;
  return awaitAgentRun(r.runPath);
}

export function awaitAgentRun(runPath) {
  return new Promise((resolve, reject) => {
    const unsub = db.doc(runPath).onSnapshot(
      (snap) => {
        const run = snap.data();
        if (!run) return;
        if (run.status === 'done') { unsub(); resolve(run.result || { summary: run.summary || '' }); }
        else if (run.status === 'error') { unsub(); reject(new Error(run.error || 'the agent run failed')); }
      },
      (err) => { unsub(); reject(err); }
    );
  });
}
// Ask the project brain: RAG over the deal's documents, deal points, and
// correspondence, with clause-level citations.
export const aiAsk = (params) => post('/legal/ask', params);
// Propose candidate answers for a doc's open agent questions from the
// project brain — options persist onto doc.agentQuestions (once per question).
export const aiProposeAnswers = (params) => post('/legal/questions/propose', params);
// Purpose audit: benchmark the current draft against deal.objective + the
// exemplar form of the target agreement type. Findings → doc.purposeAudit.
export const aiPurposeAudit = (params) => post('/legal/purpose-audit', params);
// Company structure map: compile entities, ownership/management links, and
// the agreement network into deal.structure for the dashboard diagram.
export const aiStructureMap = (params) => post('/legal/structure', params);
// Server-side version publish — used when the clause payload is too large
// for a client Firestore write (the server spills clauses to S3).
export const serverPublishVersion = (params) => post('/legal/publish', params);
// Workbook agent: with docId → versioned cell-level edit of an .xlsx
// workbook; without docId → builds a new investor-grade workbook from the
// prompt. Both publish real versions with cell-diff manifests.
export const aiWorkbookAgent = (params) => post('/legal/workbook/agent', params);
// Re-run workbook intelligence (sheet roles, model smells, AI profile).
export const aiWorkbookAnalyze = (params) => post('/legal/workbook/analyze', params);
// Full model audit: deterministic checks + AI findings FILED as a memo
// document in the project. Returns { docId, title, verdict }.
export const aiWorkbookAudit = (params) => post('/legal/workbook/audit', params);

// ---- Deal desk (backend/api/dealDesk.js) ------------------------------------
// Correspondence intake: reconcile a pasted thread against the register —
// flags matched points for review, files suggested new points, returns the
// reconciliation report.
export const aiCorrespondence = (params) => post('/legal/correspondence', params);
// Generate + file a memo document: kind 'open-items' (deterministic, from
// the register) or 'changes' (narrated from version manifests; needs docId).
export const aiGenerateMemo = (params) => post('/legal/memo', params);
// Draft a send-ready email from the deal state. Returns { subject, body } —
// never sends. audience: 'internal' | 'counterparty' | 'counsel'.
export const aiDraftEmail = (params) => post('/legal/draft-email', params);
// Verify the register against the current draft — per-point verdicts with
// evidence. Read-only.
export const aiConformance = (params) => post('/legal/conformance', params);

// ---- SAM desk (backend/api/samApi.js) ---------------------------------------
// Federal opportunity pipeline actions. Triage decisions are plain Firestore
// writes from the page; these hit the server only where it earns its keep.
export const samSync = (params) => post('/sam/sync', params);                 // org admin: pull + score now
export const samDeliverability = (params) => post('/sam/deliverability', params); // LLM gate for one notice
export const samPursue = (params) => post('/sam/pursue', params);             // spawn the pursuit project
export const samCompliance = (params) => post('/sam/compliance', params);     // solicitation → suggested points
export const samDraftResponse = (params) => post('/sam/draft-response', params); // RFI/capability draft → filed doc
// Grant workroom (backend/api/grantDesk.js)
export const grantIntake = (params) => post('/grant/intake', params);             // pipeline text → rows (dryRun) / projects
export const grantCreate = (params) => post('/grant/create', params);             // one opportunity → project
export const grantProfile = (params) => post('/grant/profile', params);           // ensure the Company Profile hub
export const grantList = (params) => post('/grant/list', params);                 // tracker rows
export const grantRequirements = (params) => post('/grant/requirements', params); // solicitation → suggested matrix rows
export const grantDraft = (params) => post('/grant/draft', params);               // queue the narrative run
export const grantReview = (params) => post('/grant/review', params);             // checks + coverage + mock panel memo

// ---- E-signature ceremony (backend/api/signApi.js) -------------------------
// Guest calls: authenticated by the opaque signing-link token (+ the OTP
// session after verification), never by a Firebase login.
async function getJson(path) {
  const res = await fetch(`${API}${path}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `${path} failed (${res.status})`);
  return data;
}
export const signSummary = (token) => getJson(`/sign/${token}`);
export const signSendCode = (token) => post(`/sign/${token}/code`, {});
export const signVerifyCode = (token, code) => post(`/sign/${token}/verify`, { code });
export const signDocs = (token, session) => post(`/sign/${token}/docs`, { session });
export const signComplete = (token, params) => post(`/sign/${token}/complete`, params);
// Sender-side (authenticated): regenerate + re-email a signer's link.
export const signResend = (params) => post('/sign/resend', params);

// ---- Secure share (backend/api/shareApi.js) --------------------------------
// Guest: token + OTP session, same model as signing. The file call returns
// PDF bytes as a Blob for an in-page viewer (never a download URL).
export const shareInfo = (token) => getJson(`/share/${token}/info`);
export const shareSendCode = (token, email) => post(`/share/${token}/code`, { email });
export const shareVerifyCode = (token, email, code) => post(`/share/${token}/verify`, { email, code });
export async function shareFileBlob(token, n, session) {
  const res = await fetch(`${API}/share/${token}/file/${n}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `file failed (${res.status})`);
  }
  return res.blob();
}
// Sender (authenticated): list a deal's links; revoke one.
export async function listSecureShares(orgId, dealId) {
  const user = firebase.auth().currentUser;
  const token = user ? await user.getIdToken() : null;
  const res = await fetch(`${API}/legal/share?orgId=${encodeURIComponent(orgId)}&dealId=${encodeURIComponent(dealId)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `share list failed (${res.status})`);
  return data.links || [];
}
export const revokeSecureShare = (params) => post('/legal/share/revoke', params);

// ---- Knowledge base (backend/api/brainApi.js) -----------------------------
// Authenticated GETs. `authedGet` is the generic form the three calls share.
async function authedGet(path) {
  const user = firebase.auth().currentUser;
  const token = user ? await user.getIdToken() : null;
  const res = await fetch(`${API}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `${path} failed (${res.status})`);
  return data;
}
const q = (o) => Object.entries(o).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
export const brainPlatform = () => authedGet('/admin/brain/platform');
export const docTypeQueue = () => authedGet('/admin/doc-types/queue');
export const docTypeAction = (params) => post('/admin/doc-types/action', params);
export const brainOrg = (orgId) => authedGet(`/legal/brain/org?${q({ orgId })}`);
export const brainInventory = (orgId, dealId) => authedGet(`/legal/brain/inventory?${q({ orgId, dealId })}`);
export const brainReindex = (params) => post('/legal/ask/reindex', params);

// Word export: downloads a .docx (clean text or tracked-changes redline) of
// any version. The clause model is canonical, so exports are always
// Word-clean regardless of what the source upload looked like.
export async function downloadWordExport(params) {
  const user = firebase.auth().currentUser;
  const token = user ? await user.getIdToken() : null;
  const res = await fetch(`${API}/legal/export`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `export failed (${res.status})`);
  }
  const blob = await res.blob();
  const dispo = res.headers.get('Content-Disposition') || '';
  const m = dispo.match(/filename\*=UTF-8''(.+)/);
  // Fallback derives the extension from the content type — never save
  // PowerPoint or PDF bytes under a .docx name.
  const type = res.headers.get('Content-Type') || '';
  const ext = type.includes('presentationml') ? 'pptx'
    : type.includes('spreadsheetml') ? 'xlsx'
      : type.includes('pdf') ? 'pdf' : 'docx';
  const filename = m ? decodeURIComponent(m[1]) : `${ext === 'pptx' ? 'presentation' : ext === 'xlsx' ? 'workbook' : 'document'}.${ext}`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Package export: one zip of clean/redline artifacts across the project's
// documents (plus the optional negotiation-points PDF) — the "responses +
// redline + clean copy" deliverable in a single download.
// params: { orgId, dealId, items: [{ docId, mode, format?, baseVersionId?,
//           folder? }], includeDealPoints?, oursPartyKey?,
//           structure? ('auto'|'flat'|'mode'|'doc' — zip folder layout) }
export async function downloadPackageExport(params) {
  const user = firebase.auth().currentUser;
  const token = user ? await user.getIdToken() : null;
  const res = await fetch(`${API}/legal/export-package`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `package export failed (${res.status})`);
  }
  const blob = await res.blob();
  const dispo = res.headers.get('Content-Disposition') || '';
  const m = dispo.match(/filename\*=UTF-8''(.+)/);
  const filename = m ? decodeURIComponent(m[1]) : 'export package.zip';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Corpus review workbook (.xlsx): documents / issues / defective acts /
// summary — available once the project's corpus review is done.
export async function downloadReviewWorkbook(params) {
  const user = firebase.auth().currentUser;
  const token = user ? await user.getIdToken() : null;
  const res = await fetch(`${API}/legal/export-review`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `review export failed (${res.status})`);
  }
  const blob = await res.blob();
  const m = (res.headers.get('Content-Disposition') || '').match(/filename\*=UTF-8''(.+)/);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = m ? decodeURIComponent(m[1]) : 'corpus review.xlsx';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Document administration — delete one document (whole version tree, server
// side), or find / remove duplicate uploads by identical text.
export const deleteDocument = (params) => post('/legal/doc/delete', params);
export const findDuplicateDocuments = (params) => post('/legal/doc/duplicates', params);
export const removeDuplicateDocuments = (params) => post('/legal/doc/duplicates/remove', params);

// Package share link: same body as downloadPackageExport plus share:true —
// the server archives the zip to S3 and returns { path, expiresDays };
// resolve to a full URL against the API base.
export async function createPackageShareLink(params) {
  const data = await post('/legal/export-package', { ...params, share: true });
  return { ...data, url: `${API}${data.path}` };
}

// Issue an equity award certificate: renders the PDF server-side, files it
// into the deal (unless file:false), and downloads it.
export async function issueCertificate(params) {
  const user = firebase.auth().currentUser;
  const token = user ? await user.getIdToken() : null;
  const res = await fetch(`${API}/legal/certificate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `certificate failed (${res.status})`);
  }
  const blob = await res.blob();
  const dispo = res.headers.get('Content-Disposition') || '';
  const m = dispo.match(/filename\*=UTF-8''(.+)/);
  const filename = m ? decodeURIComponent(m[1]) : 'certificate.pdf';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
